import { InterpolationPrimitive } from '@emotion/serialize'
import styled from '@emotion/styled'
import { ContentfulMediaMentions } from 'api/contentful/getContentfulMediaMention'
import { MediaMentionItem } from 'components/mediaMentions/MediaMentionItem'
import { Caption, Title, Title_h4_default } from 'components/Typography'
import React, { Fragment } from 'react'
import { colors } from 'utils/colors'
import { logError } from 'utils/general'
import { useWindowSize } from 'utils/hooks'
import { BREAKPOINTS } from 'utils/styled'

const STitle = styled(Caption)`
  margin-bottom: 1rem;
  @media ${BREAKPOINTS.MD.max} {
    text-align: center;
  }
`

const SLargeTitle = styled(Title)`
  text-align: center;
  max-width: 35.125rem;
  margin: 0 auto 3rem;
  @media ${BREAKPOINTS.MD.max} {
    margin-bottom: 1rem;
  }
`

const SMediaMentionsRow = styled.div<{
  addPadding?: boolean
  centerItems?: boolean
  alignItemsCenter?: boolean
  noWrapForMobile?: boolean
}>`
  margin: 0;
  position: relative;

  display: flex;
  flex-wrap: nowrap;

  ${({ alignItemsCenter }) => !alignItemsCenter && 'align-items: flex-start;'}
  ${({ centerItems }) =>
    centerItems &&
    `
      max-width: 1440px;
      margin: 0 auto;
  `}

  & > a {
    flex: 1 1 60px;
    min-width: 60px;
    ${({ addPadding }) => addPadding && 'padding: 0 1rem;'}
  }
  & > a:first-of-type {
    padding-left: 0;
  }

  @media ${BREAKPOINTS.LG.max} {
    & > a {
      padding: 0 0.5rem;
    }
  }

  @media ${BREAKPOINTS.MD.max} {
    ${(noWrapForMobile) => !noWrapForMobile && 'flex-wrap: wrap;'}

    justify-content: center;
    & > a {
      flex-basis: 30vw;
      flex-grow: 0;
    }
  }
`

export function MediaMentionsRow(props: {
  mediaMentions: ContentfulMediaMentions
  className?: string
  title?: string | null
  hideQuotes?: boolean
  noTitle?: boolean
  limit?: number
  useLargeTitle?: boolean
  centerItems?: boolean
  logoMaxHeightInRem?: number
  alignCenterItems?: boolean
  useSpaceBetween?: boolean
  titleCss?: InterpolationPrimitive
  logoCss?: InterpolationPrimitive
  noWrapForMobile?: boolean
}) {
  const { isDesktop } = useWindowSize({ isDesktop: true })
  const noCitations = !isDesktop || props.hideQuotes
  const mentions = props.mediaMentions?.items
  const itemCountToShow = props.limit != null ? props.limit : noCitations ? 7 : 5

  return (
    <div className={props.className}>
      {!props.noTitle ? (
        props.useLargeTitle ? (
          <SLargeTitle variant={Title_h4_default} color={colors.black} css={props.titleCss}>
            {props.title}
          </SLargeTitle>
        ) : (
          <STitle css={props.titleCss}>{props.title ?? 'GOODTRUST IN THE MEDIA'}</STitle>
        )
      ) : null}
      <SMediaMentionsRow
        addPadding={noCitations}
        centerItems={props.centerItems}
        alignItemsCenter={props.alignCenterItems}
        noWrapForMobile={props.noWrapForMobile}
      >
        {mentions?.slice(0, itemCountToShow)?.map((mention, index) => {
          if (!mention?.name || !mention.url || !mention.logo?.url || !mention.citation) {
            logError('Empty mention: ', mention)
            return null
          } else {
            return (
              <Fragment key={`${mention.name}_${index}`}>
                <MediaMentionItem
                  name={mention.name}
                  href={mention.url}
                  src={mention.logo?.url}
                  quote={noCitations ? undefined : mention.citation}
                  narrowText={props.centerItems}
                  logoMaxHeightInRem={props.logoMaxHeightInRem}
                  logoCss={props.logoCss}
                />
              </Fragment>
            )
          }
        })}
      </SMediaMentionsRow>
    </div>
  )
}
